import React, { useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, CardError, DeltaChip, Seg, Skel, Tween } from './parts';
import { TrendBucket, TrendPlot } from './TrendPlot';
import { dayLabel, Failed, failed, int, mins, pctChange, Visitors, VisitorsSection } from './api';

type Metric = 'sessions' | 'visitors' | 'pageviews';
const METRICS: { value: Metric; label: string; one: string; many: string }[] = [
  { value: 'sessions', label: 'Sessions', one: 'session', many: 'sessions' },
  { value: 'visitors', label: 'Visitors', one: 'visitor', many: 'visitors' },
  { value: 'pageviews', label: 'Page views', one: 'page view', many: 'page views' },
];

const GaugeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 18a8 8 0 1 1 16 0" />
    <path d="m12 18 4-5" />
  </svg>
);

/** Daily buckets, or weekly once the range passes 92 days so bars stay readable. */
const bucketise = (v: Visitors, metric: Metric): TrendBucket[] => {
  const m = METRICS.find((x) => x.value === metric)!;
  const day = v.days.map((d, i) => {
    const p = v.prevDays[i];
    return { start: d.date, d, p, tracked: d.date >= v.since, prevTracked: !!p && p.date >= v.since };
  });
  const weekly = day.length > 92;
  const groups = weekly ? Array.from({ length: Math.ceil(day.length / 7) }, (_, k) => day.slice(k * 7, k * 7 + 7)) : day.map((x) => [x]);
  return groups.map((g) => {
    const label = `${weekly ? 'w/c ' : ''}${dayLabel(g[0].start)}`;
    const sum = (k: Metric) => g.reduce((a, x) => a + x.d[k], 0);
    const tracked = g.some((x) => x.tracked);
    const prevTracked = g.some((x) => x.prevTracked);
    const prev = prevTracked ? g.reduce((a, x) => a + (x.p ? x.p[metric] : 0), 0) : null;
    if (!tracked) return { label, value: null, prev, tip: `No data · ${label}`, sub: `Visitor tracking started ${dayLabel(v.since)}` };
    const value = sum(metric);
    return {
      label,
      value,
      prev,
      tip: `${int(value)} ${value === 1 ? m.one : m.many} · ${label}`,
      sub: `${int(sum('sessions'))} sessions · ${int(sum('visitors'))} visitors · ${int(sum('pageviews'))} views${prev !== null ? ` · last period ${int(prev)}` : ''}`,
    };
  });
};

/** A headline stat with its change on the previous period (`unit` is "%" for a change, " pts" for a rate). */
const Stat: React.FC<{ k: string; v: React.ReactNode; delta: number | null; unit?: string; lowerIsBetter?: boolean; first?: boolean }> = ({ k, v, delta, unit = '%', lowerIsBetter, first }) => (
  <div className={cn('border-l border-[var(--oa-line)] px-3 md:px-[18px]', first && 'max-[900px]:border-l-0 max-[900px]:pl-0')}>
    <span className="block text-[12px] text-[var(--oa-muted)]">{k}</span>
    <b className="oa-num mt-1 flex items-center gap-2 whitespace-nowrap text-[15px] font-semibold tracking-[-0.02em] md:text-[18px]">
      {v}
      {delta !== null && Number.isFinite(delta) ? (
        <span className={cn('text-[11px] font-semibold', (lowerIsBetter ? delta <= 0 : delta >= 0) ? 'text-[var(--oa-good)]' : 'text-[var(--oa-bad)]')}>
          {delta >= 0 ? '↑' : '↓'}
          {Math.abs(delta).toFixed(0)}
          {unit}
        </span>
      ) : null}
    </b>
  </div>
);

export const VisitorsCard: React.FC<{ data: VisitorsSection | Failed | undefined; rangeDays: number }> = ({ data, rangeDays }) => {
  const [metric, setMetric] = useState<Metric>('sessions');
  const v = data && !failed(data) && data.connected ? data : null;
  const buckets = useMemo(() => (v ? bucketise(v, metric) : []), [v, metric]);
  const seg = <Seg label="Measure" value={metric} onChange={setMetric} options={METRICS.map(({ value, label }) => ({ value, label }))} />;

  if (!v) {
    return (
      <Card i={0} area="vis" stack title="Website visitors" icon={<GaugeIcon />} actions={seg}>
        {!data ? (
          <>
            <Skel className="h-[60px] w-[220px]" />
            <div className="grid flex-1 gap-8 min-[901px]:grid-cols-[1.9fr_1fr]">
              <Skel className="min-h-[230px]" />
              <Skel className="min-h-[230px]" />
            </div>
          </>
        ) : (
          <CardError message={failed(data) ? `Visitor figures did not load: ${data.error}` : 'PostHog is not connected on the API server, so visitor figures are unavailable.'} />
        )}
      </Card>
    );
  }

  const m = METRICS.find((x) => x.value === metric)!;
  const now = v.totals[metric];
  const prev = v.prevTotals[metric];
  const pct = pctChange(now, prev);
  const e = v.engagement;
  const rate = (x: { bounces: number; sessions: number }) => (x.sessions ? (x.bounces / x.sessions) * 100 : 0);
  const avg = (x: { duration: number; sessions: number }) => (x.sessions ? x.duration / x.sessions : 0);
  const per = (x: { pageviews: number; sessions: number }) => (x.sessions ? x.pageviews / x.sessions : 0);
  const hasPrev = e.prev.sessions > 0;
  const busiest = buckets.reduce<TrendBucket | null>((best, b) => (b.value !== null && (!best || b.value > (best.value ?? 0)) ? b : best), null);
  const caption =
    now === 0
      ? `No ${m.many} in these ${rangeDays} days.`
      : `${pct === null ? `Nothing to compare with yet: tracking started ${dayLabel(v.since)}.` : `${pct >= 0 ? 'Up' : 'Down'} from ${int(prev)} the previous ${rangeDays} days.`}${busiest?.value ? ` Busiest ${busiest.label.startsWith('w/c') ? 'week' : 'day'}: ${busiest.label.replace('w/c ', '')}, ${int(busiest.value)}.` : ''}`;
  const pagesTotal = Math.max(1, v.totals.pageviews);
  const pageMax = Math.max(1, ...v.topPages.map((p) => p.views));

  return (
    <Card i={0} area="vis" stack title="Website visitors" icon={<GaugeIcon />} actions={<>{seg}<DeltaChip pct={pct} /></>}>
      <div className="grid grid-cols-3 items-end gap-y-4 min-[901px]:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))]">
        <div className="col-span-3 flex flex-col gap-2 min-[901px]:col-span-1">
          <Tween className="oa-big text-[44px] md:text-[60px]" value={now} />
          <p className="oa-cap max-w-[42ch]">{caption}</p>
        </div>
        <Stat first k="Time on site" v={e.cur.sessions ? mins(avg(e.cur)) : '–'} delta={hasPrev ? pctChange(avg(e.cur), avg(e.prev)) : null} />
        <Stat k="Bounce rate" v={e.cur.sessions ? `${rate(e.cur).toFixed(0)}%` : '–'} delta={hasPrev ? rate(e.cur) - rate(e.prev) : null} unit=" pts" lowerIsBetter />
        <Stat k="Pages / visit" v={e.cur.sessions ? per(e.cur).toFixed(1) : '–'} delta={hasPrev ? pctChange(per(e.cur), per(e.prev)) : null} />
      </div>

      <div className="grid flex-1 gap-8 max-[900px]:gap-6 min-[901px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <TrendPlot buckets={buckets} label={`${m.label} per ${buckets.length !== v.days.length ? 'week' : 'day'}`} />
          <div className="oa-legend is-hide-sm">
            <span>
              <i style={{ background: 'var(--oa-accent)' }} />
              {m.label} per {buckets.length !== v.days.length ? 'week' : 'day'}
            </span>
            <span>
              <i style={{ background: 'var(--oa-text)', height: 2, borderRadius: 2 }} />
              {buckets.some((b) => b.prev !== null) ? `Previous ${rangeDays} days` : 'Previous period: not tracked yet'}
            </span>
            <span className="ml-auto text-[11.5px] text-[var(--oa-faint)]">Public site only</span>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="oa-sub">Top pages</h3>
            <span className="text-[11.5px] text-[var(--oa-faint)]">share of {int(v.totals.pageviews)} views</span>
          </div>
          {v.topPages.length === 0 ? (
            <p className="oa-cap">No pages recorded in this period.</p>
          ) : (
            <ol className="oa-bars flex-1">
              {v.topPages.map((p, i) => (
                <li key={p.path} style={{ ['--i' as string]: i } as React.CSSProperties} title={`${p.path}: ${int(p.views)} views`}>
                  <span className="truncate">{p.path === '/' ? 'Home' : p.path}</span>
                  <b>{Math.round((p.views / pagesTotal) * 100)}%</b>
                  <i style={{ ['--w' as string]: `${(p.views / pageMax) * 100}%` } as React.CSSProperties} />
                  <em>{int(p.views)} views</em>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </Card>
  );
};
